// 평가(나의 HACCP 인증) / 유지하기 API 호출 모음.
// 모든 호출은 apiFetch<T>를 통해 JWT·refresh 횡단 관심사를 재사용한다.

import { apiFetch, apiRequest } from '~/api/client'
import { readDelimitedStream, sseData } from '~/lib/stream'
import type {
    AssessmentCombination,
    AssessmentReport,
    AssessmentResponse,
    ChecklistItem,
    CreateAssessmentRequest,
    CreateMaintenanceRequest,
    DefineAssessmentResponse,
    DefineMessage,
    IndustryOption,
    ItemScore,
    MaintenanceEvent,
    MaintenanceClarification,
    MaintenanceTurnRequest,
    MaintenanceTurnResponse,
    SaveAnswersRequest,
    SurveyAnswer,
    SurveyTurn,
    SurveyTurnRequest,
} from '~/api/types'

/**
 * 업종 선택지 목록.
 * 규모·분류·단계를 넘기면 그 조합에 실제 항목이 있는 업종만 돌려받는다(없는 조합 선택 차단).
 */
export function listIndustries(params?: {
    scale?: string
    classification?: string
    assessmentType?: string
}): Promise<IndustryOption[]> {
    const q = new URLSearchParams()
    if (params?.scale) q.set('scale', params.scale)
    if (params?.classification) q.set('classification', params.classification)
    if (params?.assessmentType) q.set('assessment_type', params.assessmentType)
    const qs = q.toString()
    return apiFetch<IndustryOption[]>(
        `/assessments/industries${qs ? `?${qs}` : ''}`
    )
}

/**
 * 실존하는 (규모·분류·단계·업종) 조합 전부.
 * 새 평가 폼이 4축 상호제약(cascading) 선택을 구동해 죽은 조합을 아예 노출하지 않게 한다.
 */
export function listCombinations(): Promise<AssessmentCombination[]> {
    return apiFetch<AssessmentCombination[]>('/assessments/combinations')
}

/** 새 평가 생성. */
export function createAssessment(
    body: CreateAssessmentRequest
): Promise<AssessmentResponse> {
    return apiFetch<AssessmentResponse>('/assessments', {
        method: 'POST',
        body,
    })
}

/**
 * AI로 정의 — 지금까지 대화 전체를 보내 다음 질문/보기와 추론된 폼 슬롯을 받는다.
 * 첫 진입은 `defineAssessment([])`로 첫 질문부터 시작한다.
 */
export function defineAssessment(
    messages: DefineMessage[]
): Promise<DefineAssessmentResponse> {
    return apiFetch<DefineAssessmentResponse>('/assessments/define', {
        method: 'POST',
        body: { messages },
    })
}

/** 저장된 평가 목록. */
export function listAssessments(): Promise<AssessmentResponse[]> {
    return apiFetch<AssessmentResponse[]>('/assessments')
}

/** 평가 단건 조회. */
export function getAssessment(id: string): Promise<AssessmentResponse> {
    return apiFetch<AssessmentResponse>(`/assessments/${id}`)
}

/** 평가 제목 수정. 채점 결과·상태는 그대로 유지된다. */
export function updateAssessment(
    id: string,
    body: { title: string }
): Promise<AssessmentResponse> {
    return apiFetch<AssessmentResponse>(`/assessments/${id}`, {
        method: 'PATCH',
        body,
    })
}

/** 평가 삭제. 답안·점수·보고서도 함께 제거된다. */
export function deleteAssessment(id: string): Promise<void> {
    return apiFetch<void>(`/assessments/${id}`, { method: 'DELETE' })
}

/** 평가 체크리스트(항목·필드) 조회. */
export function getChecklist(id: string): Promise<ChecklistItem[]> {
    return apiFetch<ChecklistItem[]>(`/assessments/${id}/checklist`)
}

/** 현재 답안(필드별) 조회 — 전체 문항 목록에 답을 표시하는 데 쓴다. */
export function getAnswers(id: string): Promise<SurveyAnswer[]> {
    return apiFetch<SurveyAnswer[]>(`/assessments/${id}/answers`)
}

/** 답안 저장(전체 모음, 204). */
export function saveAnswers(
    id: string,
    body: SaveAnswersRequest
): Promise<void> {
    return apiFetch<void>(`/assessments/${id}/answers`, { method: 'PUT', body })
}

/**
 * 대화형 설문 한 턴 진행.
 * - 첫 진입: `surveyTurn(id, {})`로 업종 질문부터 시작한다.
 * - 답변 전송: `{ message }`.
 * - 업종 확정(드롭다운 폴백): `{ industry }`.
 */
export function surveyTurn(
    id: string,
    body: SurveyTurnRequest
): Promise<SurveyTurn> {
    return apiFetch<SurveyTurn>(`/assessments/${id}/survey`, {
        method: 'POST',
        body,
    })
}

/** 채점 실행. */
export function scoreAssessment(id: string): Promise<AssessmentResponse> {
    return apiFetch<AssessmentResponse>(`/assessments/${id}/score`, {
        method: 'POST',
    })
}

/** 항목별 채점 결과. */
export function getScores(id: string): Promise<ItemScore[]> {
    return apiFetch<ItemScore[]>(`/assessments/${id}/scores`)
}

/** 보고서 생성. */
export function createReport(id: string): Promise<AssessmentReport> {
    return apiFetch<AssessmentReport>(`/assessments/${id}/report`, {
        method: 'POST',
    })
}

/** 보고서 조회. */
export function getReport(id: string): Promise<AssessmentReport> {
    return apiFetch<AssessmentReport>(`/assessments/${id}/report`)
}

/** 유지하기 변경 등록(영향 분석). */
export function createMaintenance(
    id: string,
    body: CreateMaintenanceRequest
): Promise<MaintenanceEvent> {
    return apiFetch<MaintenanceEvent>(`/assessments/${id}/maintenance`, {
        method: 'POST',
        body,
    })
}

type StreamEventShape = {
    type?: string
    affected?: unknown[]
    guide?: string
    event?: MaintenanceEvent
    message?: string
}

/**
 * 변경 영향 분석 스트리밍(SSE). 영향 항목이 생성되는 대로 onPartial로 흘리고, 완료 시 저장된
 * 이력 이벤트를 반환한다. 스트림 시작 실패·파싱 불가·서버 error 이벤트 시 예외를 던져
 * 호출측이 비스트리밍 경로(createMaintenance)로 폴백하게 한다.
 */
export async function streamMaintenanceChange(
    id: string,
    body: CreateMaintenanceRequest,
    onPartial?: (p: { affectedCount: number; guide: string }) => void
): Promise<MaintenanceEvent> {
    const res = await apiRequest(`/assessments/${id}/maintenance/stream`, {
        method: 'POST',
        body,
        headers: { Accept: 'text/event-stream' },
    })
    if (!res.ok || !res.body) {
        throw new Error(
            `스트리밍 분석을 시작하지 못했어요 (status ${res.status})`
        )
    }

    let event: MaintenanceEvent | null = null
    let streamError: string | null = null
    for await (const line of readDelimitedStream(res.body)) {
        const raw = sseData(line)
        if (!raw) continue
        let ev: StreamEventShape
        try {
            ev = JSON.parse(raw) as StreamEventShape
        } catch {
            continue
        }
        if (ev.type === 'partial') {
            onPartial?.({
                affectedCount: ev.affected?.length ?? 0,
                guide: ev.guide ?? '',
            })
        } else if (ev.type === 'done' && ev.event) {
            event = ev.event
        } else if (ev.type === 'error') {
            streamError = ev.message || '스트리밍 분석 중 오류가 발생했어요.'
        }
    }

    if (streamError) throw new Error(streamError)
    if (!event) throw new Error('분석 결과를 받지 못했어요.')
    return event
}

/** 유지하기 변경 등록 전 추가 확인 질문 생성. */
export function clarifyMaintenance(
    id: string,
    body: CreateMaintenanceRequest
): Promise<MaintenanceClarification> {
    return apiFetch<MaintenanceClarification>(
        `/assessments/${id}/maintenance/clarify`,
        {
            method: 'POST',
            body,
        }
    )
}

/** 유지하기 이력. */
export function listMaintenance(id: string): Promise<MaintenanceEvent[]> {
    return apiFetch<MaintenanceEvent[]>(`/assessments/${id}/maintenance`)
}

/** 유지하기 특정 변경건 후속 대화. */
export function continueMaintenanceTurn(
    assessmentId: string,
    eventId: string,
    body: MaintenanceTurnRequest
): Promise<MaintenanceTurnResponse> {
    return apiFetch<MaintenanceTurnResponse>(
        `/assessments/${assessmentId}/maintenance/${eventId}/turn`,
        {
            method: 'POST',
            body,
        }
    )
}
